import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, CTABand, Reveal, Eyebrow } from "@/components/chrome";
import { SITE } from "@/data/site";
import { PROVINCES, CITY_COUNT } from "@/data/provinces";
import { SERVICES } from "@/data/services";
import { paths } from "@/lib/seo";

const VALUES = [
  { n: "01", t: "Show up", d: "On time, in uniform, with everything needed. Reliability is the foundation everything else stands on." },
  { n: "02", t: "Own the result", d: "Every visit ends with a documented quality check. If it isn't right, we return and make it right — free." },
  { n: "03", t: "Respect the space", d: "Homes and workplaces are personal. Background-checked, insured professionals only — never subcontracted strangers." },
  { n: "04", t: "Keep it honest", d: "Flat rates quoted upfront. No meters, no upsells, no fine print. The price we say is the price you pay." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <PageHero prefix="about"
        crumbs={[{ name: "Home", path: "/" }, { name: "About" }]}
        eyebrow="About Tbay Cleaning Services"
        title="Canadian-owned. Coast to coast."
        lead={`${SITE.name} started in ${SITE.founded} as a two-person crew in Northern Ontario. Today we're a national network of vetted local cleaning teams in ${CITY_COUNT}+ communities — run with the same no-nonsense Canadian straightforwardness.`} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="about-story">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="space-y-5">
            <Eyebrow>Our story</Eyebrow>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Built on a simple observation
            </h2>
            <p className="leading-relaxed text-slate-600">
              Canadians couldn't find a cleaning company that answered the phone, quoted honestly, and showed up
              the same way every time — especially outside the biggest metros. So we built one: local crews who
              know their communities, backed by national standards, training, and supervision.
            </p>
            <p className="leading-relaxed text-slate-600">
              Every crew member is an employee — background-checked, bonded, insured, and trained on our 50-point
              checklist. Every account gets a named supervisor and monthly quality audits. And every quote is a
              firm flat rate delivered by a real person, usually within one business hour.
            </p>
          </div>
          <div className="grid content-start gap-6 sm:grid-cols-2">
            {[["13", "Provinces & territories"], [`${CITY_COUNT}+`, "Cities served"], ["12", "Specialized services"], ["50-pt", "Quality checklist"]].map(([n, l]) => (
              <div key={l} className="rounded-2xl border border-slate-200 bg-white p-7">
                <p className="font-display text-4xl font-extrabold tracking-tight text-slate-900">{n}</p>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="about-values">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20">
          <Reveal><Eyebrow>How we work</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Four promises, kept daily</h2></Reveal>
          <div className="mt-12 grid gap-x-14 gap-y-12 md:grid-cols-2">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={i * 0.08} className="border-t border-slate-200 pt-6">
                <p className="font-mono text-sm font-semibold text-cyan-700">{v.n}</p>
                <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-slate-900">{v.t}</h3>
                <p className="mt-3 leading-relaxed text-slate-600">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 sm:py-20" data-testid="about-links">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <Eyebrow>Services</Eyebrow>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <Link key={s.slug} to={paths.service(s.slug)} className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
                  {s.name} <ArrowRight className="h-3 w-3 text-slate-300" />
                </Link>
              ))}
            </div>
          </div>
          <div>
            <Eyebrow>Coverage</Eyebrow>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
              {PROVINCES.map((p) => (
                <Link key={p.slug} to={paths.province(p.slug)} className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
                  {p.name} <ArrowRight className="h-3 w-3 text-slate-300" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABand title="Talk to a real coordinator"
        lead={`Call ${SITE.phone} or email ${SITE.email} — no forms, no waiting. We answer like the small-town company we still are.`}
        prefix="about-cta" />
    </div>
  );
}
